import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react';

export type ToastTone = 'success' | 'error' | 'info';

interface Toast {
	id: number;
	message: string;
	tone: ToastTone;
}

interface ToastContextValue {
	notify: (message: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_DURATION_MS = 2600;
const MAX_VISIBLE = 3;

const TONE_STYLES: Record<ToastTone, { icon: string; iconClass: string }> = {
	success: { icon: '✓', iconClass: 'bg-aqua text-surface' },
	error: { icon: '!', iconClass: 'bg-accent text-white' },
	info: { icon: 'i', iconClass: 'bg-iris text-white' },
};

export function ToastProvider({ children }: { children: ReactNode }) {
	const [toasts, setToasts] = useState<Toast[]>([]);
	const nextId = useRef(0);
	const timers = useRef(new Set<number>());

	const notify = useCallback((message: string, tone: ToastTone = 'success') => {
		const id = nextId.current++;
		setToasts((current) => [...current, { id, message, tone }].slice(-MAX_VISIBLE));

		const timer = window.setTimeout(() => {
			setToasts((current) => current.filter((toast) => toast.id !== id));
			timers.current.delete(timer);
		}, TOAST_DURATION_MS);
		timers.current.add(timer);
	}, []);

	// Dọn timer khi unmount để không setState trên component đã tháo.
	useEffect(
		() => () => {
			for (const timer of timers.current) window.clearTimeout(timer);
			timers.current.clear();
		},
		[],
	);

	const value = useMemo(() => ({ notify }), [notify]);

	return (
		<ToastContext.Provider value={value}>
			{children}
			<div
				className="pointer-events-none fixed right-5 bottom-5 z-50 flex flex-col items-end gap-2"
				role="status"
				aria-live="polite"
			>
				{toasts.map((toast) => (
					<div
						key={toast.id}
						className="animate-rise pointer-events-auto flex items-center gap-2.5 rounded-[10px] border border-white/10 bg-[#202238] px-4 py-3 text-sm shadow-neko"
					>
						<span
							className={`grid size-[22px] shrink-0 place-items-center rounded-full text-xs font-extrabold ${TONE_STYLES[toast.tone].iconClass}`}
							aria-hidden="true"
						>
							{TONE_STYLES[toast.tone].icon}
						</span>
						{toast.message}
					</div>
				))}
			</div>
		</ToastContext.Provider>
	);
}

export function useToast(): ToastContextValue {
	const context = useContext(ToastContext);
	if (!context) {
		throw new Error('useToast phải được dùng bên trong <ToastProvider>.');
	}
	return context;
}
